import axios from "axios";

const BASE_URL =
  "https://backdanlu.eu-4.evennode.com";

const getToken = () =>
  localStorage.getItem("token") || "";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

/* =========================================
   TOKEN
========================================= */

api.interceptors.request.use(
  (config) => {
    config.headers =
      config.headers || {};

    config.headers.token =
      getToken();

    return config;
  }
);

/* =========================================
   ERROR NORMALIZADO
========================================= */

const normalizarError = (
  error,
  fallback
) => {
  return {
    ok: false,

    error:
      error?.response?.data
        ?.error ||
      error?.response?.data
        ?.message ||
      error?.response?.data
        ?.err?.message ||
      error?.message ||
      fallback ||
      "Error de comunicación",
  };
};

/* =========================================================
   PÓLIZAS
========================================================= */

/* =========================================
   LISTAR PÓLIZAS

   Filtros opcionales:
   {
     estado,
     compania,
     asegurado,
     patente,
     q,
     incluirInactivas
   }
========================================= */

export const getPolizasSeguros =
  async (params = {}) => {
    try {
      const { data } =
        await api.get(
          "/seguros/polizas",
          {
            params,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener las pólizas."
      );
    }
  };

/* =========================================
   OBTENER UNA PÓLIZA

   Devuelve también las cuotas asociadas.
========================================= */

export const getPolizaSeguro =
  async (id) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador de la póliza.",
        };
      }

      const { data } =
        await api.get(
          `/seguros/polizas/${id}`
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo obtener la póliza."
      );
    }
  };

/* =========================================
   CREAR PÓLIZA
========================================= */

export const crearPolizaSeguro =
  async (payload) => {
    try {
      const { data } =
        await api.post(
          "/seguros/polizas",
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo crear la póliza."
      );
    }
  };

/* =========================================
   EDITAR PÓLIZA
========================================= */

export const editarPolizaSeguro =
  async (
    id,
    payload
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador de la póliza.",
        };
      }

      const { data } =
        await api.put(
          `/seguros/polizas/${id}`,
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo modificar la póliza."
      );
    }
  };

/* =========================================================
   CUOTAS / FACTURACIONES / ENDOSOS
========================================================= */

/* =========================================
   LISTAR CUOTAS

   Filtros opcionales:
   {
     poliza,
     estado,
     desde,
     hasta
   }
========================================= */

export const getCuotasSeguros =
  async (params = {}) => {
    try {
      const { data } =
        await api.get(
          "/seguros/cuotas",
          {
            params,
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener las cuotas de seguros."
      );
    }
  };

/* =========================================
   CREAR CUOTA / FACTURACIÓN / ENDOSO
========================================= */

export const crearCuotaSeguro =
  async (payload) => {
    try {
      const { data } =
        await api.post(
          "/seguros/cuotas",
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo crear la cuota de seguro."
      );
    }
  };

/* =========================================
   EDITAR CUOTA
========================================= */

export const editarCuotaSeguro =
  async (
    id,
    payload
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador de la cuota.",
        };
      }

      const { data } =
        await api.put(
          `/seguros/cuotas/${id}`,
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo modificar la cuota de seguro."
      );
    }
  };

/* =========================================================
   PAGOS DE SEGUROS
========================================================= */

/* =========================================
   PAGAR DOCUMENTO / CUOTA

   Payload ejemplo:

   {
     medioPago: "CUENTA_BANCARIA",
     cuentaBancaria: "...",
     fechaPago: "2026-09-24",
     importe: 620123.98
   }

   También admite:
   CAJA
   CHEQUE
   ECHEQ
========================================= */

export const pagarCuotaSeguro =
  async (
    id,
    payload
  ) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del documento.",
        };
      }

      const { data } =
        await api.post(
          `/seguros/cuotas/${id}/pagar`,
          payload
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo registrar el pago del seguro."
      );
    }
  };

/* =========================================
   ANULAR PAGO

   Revierte:
   - Caja
   - Banco
   - Cheque/eCheq
   - Movimiento

   y vuelve el documento a pendiente.
========================================= */

export const anularPagoCuotaSeguro =
  async (id) => {
    try {
      if (!id) {
        return {
          ok: false,
          error:
            "Falta el identificador del documento.",
        };
      }

      const { data } =
        await api.post(
          `/seguros/cuotas/${id}/anular-pago`,
          {}
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudo anular el pago del seguro."
      );
    }
  };

/* =========================================================
   VEHÍCULOS PARA VINCULAR SEGUROS
========================================================= */

export const getCamionesParaSeguros =
  async () => {
    try {
      const { data } =
        await api.get(
          "/camiones",
          {
            params: {
              incluirInactivos: 1,
            },
          }
        );

      return data;
    } catch (error) {
      return normalizarError(
        error,
        "No se pudieron obtener los vehículos."
      );
    }
  };